import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useSettingsQuery } from "@/api/queries/admin/SettingsQueries";
import { useUpdateSettingsMutation, useTestEmailMutation } from "@/api/mutations/admin/SettingsMutations";
import { toast } from "sonner";
import { Loader2, Mail, Send, Server, CheckCircle2, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { settingsValidator, type SmtpFormValues } from "@shared/validators/admin/settings-validators";

const SmtpSettingsSection = () => {
    const { t } = useTranslation();
    const { data: settingsData, isLoading: isLoadingSettings } = useSettingsQuery();
    const { mutate: updateSettings, isPending: isUpdating } = useUpdateSettingsMutation();
    const testEmailMutation = useTestEmailMutation();
    const [testEmailAddress, setTestEmailAddress] = useState("");

    const form = useForm<SmtpFormValues>({
        resolver: zodResolver(settingsValidator.smtpForm),
        defaultValues: {
            smtp_enabled: false,
            smtp_host: "",
            smtp_port: 587,
            smtp_secure: false,
            smtp_user: "",
            smtp_password: "",
            from_email: "",
            from_name: "",
        },
    });

    useEffect(() => {
        if (settingsData?.notification_settings) {
            const notificationSettings = settingsData.notification_settings as any;
            form.reset({
                smtp_enabled: notificationSettings.email_enabled ?? false,
                smtp_host: notificationSettings.email_config?.smtp_host ?? "",
                smtp_port: notificationSettings.email_config?.smtp_port ?? 587,
                smtp_secure: notificationSettings.email_config?.smtp_secure ?? false,
                smtp_user: notificationSettings.email_config?.smtp_user ?? "",
                smtp_password: notificationSettings.email_config?.smtp_password ?? "",
                from_email: notificationSettings.email_config?.from_email ?? "",
                from_name: notificationSettings.email_config?.from_name ?? "",
            });
        }
    }, [settingsData, form]);

    const onSubmit = (data: SmtpFormValues) => {
        // Get existing notification settings with defaults for required fields
        const existingNotificationSettings = (settingsData?.notification_settings as any) || {};
        
        updateSettings(
            {
                notification_settings: {
                    // Preserve existing webhook and flow settings
                    webhook_enabled: existingNotificationSettings.webhook_enabled ?? false,
                    webhook_config: existingNotificationSettings.webhook_config ?? {
                        url: "",
                        secret: "",
                        headers: {},
                    },
                    flows: existingNotificationSettings.flows ?? [],
                    // Update email/SMTP settings
                    email_enabled: data.smtp_enabled,
                    email_provider: "smtp",
                    email_config: {
                        ...(existingNotificationSettings.email_config || {}),
                        smtp_host: data.smtp_host,
                        smtp_port: data.smtp_port,
                        smtp_secure: data.smtp_secure,
                        smtp_user: data.smtp_user,
                        smtp_password: data.smtp_password,
                        from_email: data.from_email,
                        from_name: data.from_name,
                    },
                },
            },
            {
                onSuccess: () => {
                    toast.success(t("settings.smtpSettings.messages.settingsUpdated"));
                },
                onError: () => {
                    toast.error(t("settings.smtpSettings.messages.settingsUpdateFailed"));
                },
            }
        );
    };

    const handleTestEmail = async () => {
        if (!testEmailAddress || !testEmailAddress.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            toast.error(t("settings.smtpSettings.messages.validEmailRequired"));
            return;
        }

        testEmailMutation.mutate(
            { email: testEmailAddress },
            {
                onSuccess: () => {
                    toast.success(t("settings.smtpSettings.messages.testEmailSent", { email: testEmailAddress }));
                    setTestEmailAddress("");
                },
                onError: (error: any) => {
                    toast.error(error.message || t("settings.smtpSettings.messages.testEmailFailed"));
                },
            }
        );
    };

    if (isLoadingSettings) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    const smtpEnabled = form.watch("smtp_enabled");
    const smtpHost = form.watch("smtp_host");
    const fromEmail = form.watch("from_email");
    
    const isConfigured = smtpEnabled && smtpHost && fromEmail;

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h2 className="text-2xl font-bold">{t("settings.smtpSettings.title")}</h2>
                <p className="text-base text-muted-foreground">
                    {t("settings.smtpSettings.description")}
                </p>
            </div>

            {/* Status Banner */}
            <Alert variant={isConfigured ? "default" : "destructive"} className={isConfigured ? "border-green-500/50 bg-green-500/10" : ""}>
                {isConfigured ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                    <AlertCircle className="h-4 w-4" />
                )}
                <AlertTitle className="flex items-center gap-2">
                    {t("settings.smtpSettings.status.title")}
                    <Badge variant={isConfigured ? "default" : "destructive"} className={isConfigured ? "bg-green-500" : ""}>
                        {isConfigured ? t("settings.smtpSettings.status.configured") : t("settings.smtpSettings.status.notConfigured")}
                    </Badge>
                </AlertTitle>
                <AlertDescription>
                    {isConfigured 
                        ? t("settings.smtpSettings.status.configuredDescription")
                        : t("settings.smtpSettings.status.notConfiguredDescription")
                    }
                </AlertDescription>
            </Alert>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    {/* SMTP Server Configuration */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <Server className="h-5 w-5" />
                                <CardTitle>{t("settings.smtpSettings.serverConfiguration.title")}</CardTitle>
                            </div>
                            <CardDescription>
                                {t("settings.smtpSettings.serverConfiguration.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <FormField
                                control={form.control}
                                name="smtp_enabled"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base">{t("settings.smtpSettings.serverConfiguration.enableSmtp")}</FormLabel>
                                            <FormDescription>
                                                {t("settings.smtpSettings.serverConfiguration.enableSmtpDescription")}
                                            </FormDescription>
                                        </div>
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            {smtpEnabled && (
                                <>
                                    <Separator />
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="smtp_host"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("settings.smtpSettings.serverConfiguration.smtpHost")}</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="smtp.example.com" {...field} />
                                                    </FormControl>
                                                    <FormDescription>{t("settings.smtpSettings.serverConfiguration.smtpHostDescription")}</FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="smtp_port"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("settings.smtpSettings.serverConfiguration.smtpPort")}</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder="587"
                                                            {...field}
                                                            onChange={(e) => field.onChange(parseInt(e.target.value) || 587)}
                                                        />
                                                    </FormControl>
                                                    <FormDescription>{t("settings.smtpSettings.serverConfiguration.smtpPortDescription")}</FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <FormField
                                        control={form.control}
                                        name="smtp_secure"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                                <div className="space-y-0.5">
                                                    <FormLabel>{t("settings.smtpSettings.serverConfiguration.useTls")}</FormLabel>
                                                    <FormDescription>{t("settings.smtpSettings.serverConfiguration.useTlsDescription")}</FormDescription>
                                                </div>
                                                <FormControl>
                                                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />

                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="smtp_user"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("settings.smtpSettings.serverConfiguration.smtpUsername")}</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="username" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="smtp_password"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("settings.smtpSettings.serverConfiguration.smtpPassword")}</FormLabel>
                                                    <FormControl>
                                                        <Input type="password" placeholder="••••••••" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* Sender Configuration */}
                    {smtpEnabled && (
                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-2">
                                    <Mail className="h-5 w-5" />
                                    <CardTitle>{t("settings.smtpSettings.senderConfiguration.title")}</CardTitle>
                                </div>
                                <CardDescription>
                                    {t("settings.smtpSettings.senderConfiguration.description")}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="from_email"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("settings.smtpSettings.senderConfiguration.fromEmail")}</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="noreply@example.com" {...field} />
                                                </FormControl>
                                                <FormDescription>{t("settings.smtpSettings.senderConfiguration.fromEmailDescription")}</FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="from_name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("settings.smtpSettings.senderConfiguration.fromName")}</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Junet" {...field} />
                                                </FormControl>
                                                <FormDescription>{t("settings.smtpSettings.senderConfiguration.fromNameDescription")}</FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Test Email */}
                    {smtpEnabled && (
                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-2">
                                    <Send className="h-5 w-5" />
                                    <CardTitle>{t("settings.smtpSettings.testEmail.title")}</CardTitle>
                                </div>
                                <CardDescription>
                                    {t("settings.smtpSettings.testEmail.description")}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="test@example.com"
                                        value={testEmailAddress}
                                        onChange={(e) => setTestEmailAddress(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleTestEmail();
                                            }
                                        }}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleTestEmail}
                                        disabled={testEmailMutation.isPending || !testEmailAddress}
                                    >
                                        {testEmailMutation.isPending ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                {t("settings.smtpSettings.testEmail.sending")}
                                            </>
                                        ) : (
                                            <>
                                                <Send className="mr-2 h-4 w-4" />
                                                {t("settings.smtpSettings.testEmail.sendTest")}
                                            </>
                                        )}
                                    </Button>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {t("settings.smtpSettings.testEmail.hint")}
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Save Button */}
                    <div className="flex justify-end">
                        <Button type="submit" disabled={isUpdating}>
                            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isUpdating ? t("common.loading") : t("common.save")}
                        </Button>
                    </div>
                </form>
            </Form>
        </div>
    );
};

export default SmtpSettingsSection;

